import { createContext, useEffect, useState } from "react";
import { Slide, ToastContainer } from "react-toastify";
import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router";
import { gql, useQuery } from "@apollo/client";
import { ThemeProvider } from "@mui/material/styles";
import { WelcomePageContainer } from "./pages/welcome-page/welcome-page-container";
import { LoginPageContainer } from "./pages/login-page/login-page-container";
import { ResetPasswordPageContainer } from "./pages/reset-password-page/reset-password-page-container";
import { GoogleCallbackPageContainer } from "./pages/google-callback-page/google-callback-page-container";
import { HomePageContainer } from "./pages/home-page/home-page-container";
import { ExpensesPageContainer } from "./pages/expenses-page/expenses-page-container";
import { InsightsPageContainer } from "./pages/insights-page/insights-page-container";
import { CategoriesPageContainer } from "./pages/categories-page/categories-page-container";
import { SavingGoalsPageContainer } from "./pages/saving-goals-page/saving-goals-page-container";
import { InvestmentsPageContainer } from "./pages/investments-page/investments-page-container";
import { ConfirmEmailPageContainer } from "./pages/confirm-email-page/confirm-email-page-container";
import { Header } from "./components/layout";
import { AUTH_TOKEN, AUTH_TOKEN_USER } from "./constants";
import { handleLogout } from "./utils/handleLogout";
import { isAuthFailure } from "./utils/session-state";
import { ContentWrapperStyled } from "./shared";
import { PrivacyPageContainer } from "./pages/privacy-page/privacy-page-container";
import { TermsPageContainer } from "./pages/terms-page/terms-page-container";
import { AboutPageContainer } from "./pages/about-page/about-page-container";
import { ProfilePageContainer } from "./pages/profile-page/profile-page-container";
import { ReportsPageContainer } from "./pages/reports-page/reports-page-container";
import { GroupsPageContainer } from "./pages/groups-page/groups-page-container";
import { AcceptInvitePageContainer } from "./pages/accept-invite-page/accept-invite-page-container";
import { PendingInviteResume } from "./pages/accept-invite-page/pending-invite-resume";
import { ScopeProvider } from "./features/groups/scope-context";
import { SessionError } from "./components/session-error/session-error";
import { OnboardingProvider } from "./features/demo/onboarding";
import { DemoBanner } from "./features/demo/demo-banner";
import { CookieConsentBanner } from "./components/cookie-consent/cookie-consent-banner";
import { analytics } from "./utils/analytics";
import { theme } from "./theme";

export const GET_USER_ME = gql`
  query Me {
    me {
      id
      email
      currency
      weeklyReminder
      name
      picture
      provider
      # NULL until the first-run tour has been finished or dismissed. On the
      # user rather than in localStorage, so it does not replay per device.
      onboardingSeenAt
    }
  }
`;

export const UserContext = createContext("");

function App() {
  const currentDate = new Date();
  const [pageDate, setPageDate] = useState(currentDate);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem(AUTH_TOKEN),
  );

  const {
    data: userData,
    loading: userMeLoading,
    error: userMeError,
    refetch: refetchUserData,
  } = useQuery(GET_USER_ME, {
    skip: !token,
    onCompleted: (data) => {
      if (data?.me) {
        // Identify user in Mixpanel
        analytics.identify(data.me.id);
        analytics.setUserProperties({
          $email: data.me.email,
          currency: data.me.currency || "USD",
          // Add any other user properties here
        });
      }
    },
  });

  /*
   * `me` is the gate for every authenticated route, because every one of them
   * reads the user — `/groups` wants `userData.id`, `/settings` wants
   * `userData.currency`. Rendering them without one is not a degraded
   * experience, it is a `TypeError` and a white screen.
   *
   * This used to be checked only against `networkError` containing "invalid
   * token". But an expired or rejected token makes the server throw
   * `Unauthenticated!`, and Apollo Server returns that as **HTTP 200 with a
   * `graphQLErrors` array** — so the check never fired, `userData` stayed
   * undefined, and the app crashed on the first page that touched it. Tokens
   * last 90 days, so that was every user eventually, with a timer on it.
   */
  const sessionUser = userData?.me;
  const sessionUnusable = Boolean(token) && !userMeLoading && !sessionUser;

  // Auth-shaped failures throw the session away; anything else is probably the
  // server having a moment, and logging someone out over that is an
  // overreaction they cannot undo without their password.
  const looksLikeAuthFailure = isAuthFailure(userMeError);

  useEffect(() => {
    if (!token) return;
    // A token with no stored user is a half-cleared session from an older
    // logout; it can never resolve on its own.
    if (!localStorage.getItem(AUTH_TOKEN_USER)) return handleLogout();
    if (sessionUnusable && looksLikeAuthFailure) handleLogout();
  }, [token, sessionUnusable, looksLikeAuthFailure]);

  if (userMeLoading) {
    return null;
  }

  if (sessionUnusable) {
    // Logging out navigates away; render nothing rather than flashing an error
    // at someone who is already on their way to the login page.
    return looksLikeAuthFailure ? null : (
      <ThemeProvider theme={theme}>
        <SessionError onRetry={() => refetchUserData()} />
      </ThemeProvider>
    );
  }

  const onClickNext = () => {
    const nextDate = new Date(
      pageDate.getFullYear(),
      pageDate.getMonth() + 1,
      pageDate.getDate(),
    );
    setPageDate(nextDate);
  };

  const onClickPrevious = () => {
    const previousDate = new Date(
      pageDate.getFullYear(),
      pageDate.getMonth() - 1,
      pageDate.getDate(),
    );
    setPageDate(previousDate);
  };

  const subdomain = window.location.hostname.split(".").slice(0, -2).join(".");
  const isAppSubdomain = subdomain === "app";
  const isDevelopment = import.meta.env.MODE === "development";

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <Router>
          <Routes>
            {/* this is website domain */}
            {!isAppSubdomain && !isDevelopment && (
              <>
                <Route path="/" element={<WelcomePageContainer />} />
                <Route path="/privacy" element={<PrivacyPageContainer />} />
                <Route path="/terms" element={<TermsPageContainer />} />
                <Route path="/about" element={<AboutPageContainer />} />
              </>
            )}

            {/* this is app domain */}
            {(isAppSubdomain || isDevelopment) && (
              <>
                <Route path="/welcome" element={<WelcomePageContainer />} />
                <Route path="/privacy" element={<PrivacyPageContainer />} />
                <Route path="/terms" element={<TermsPageContainer />} />
                <Route path="/about" element={<AboutPageContainer />} />

                <Route
                  path="/reset-password"
                  element={<ResetPasswordPageContainer />}
                />

                <Route
                  path="/confirm-email"
                  element={<ConfirmEmailPageContainer />}
                />

                <Route
                  path="/accept-invite"
                  element={<AcceptInvitePageContainer />}
                />

                <Route
                  path="/auth/google/callback"
                  element={<GoogleCallbackPageContainer setToken={setToken} />}
                />

                {!token && (
                  <Route
                    path="/"
                    element={<LoginPageContainer setToken={setToken} />}
                  />
                )}

                {token && (
                  <>
                    <Route
                      element={
                        <UserContext.Provider value={sessionUser.currency}>
                          <OnboardingProvider
                            user={sessionUser}
                            onUserChanged={refetchUserData}
                          >
                            <Header
                              onLogout={handleLogout}
                              userData={sessionUser}
                            />
                            {/* Below the header so it never displaces the
                                brand, above everything else so there is no
                                screen from which the demo cannot be left. */}
                            <DemoBanner />
                            <PendingInviteResume />
                            <ScopeProvider>
                              <ContentWrapperStyled>
                                <Outlet />
                              </ContentWrapperStyled>
                            </ScopeProvider>
                          </OnboardingProvider>
                        </UserContext.Provider>
                      }
                    >
                      <Route
                        path="/"
                        element={
                          <HomePageContainer
                            pageDate={pageDate}
                            onClickNext={onClickNext}
                            onClickPrevious={onClickPrevious}
                          />
                        }
                      />
                      <Route
                        path="/expenses"
                        element={
                          <ExpensesPageContainer
                            pageDate={pageDate}
                            onClickNext={onClickNext}
                            onClickPrevious={onClickPrevious}
                          />
                        }
                      />
                      <Route
                        path="/budget"
                        element={<CategoriesPageContainer />}
                      />
                      <Route
                        path="/insights"
                        element={
                          <InsightsPageContainer
                            pageDate={pageDate}
                            onClickNext={onClickNext}
                            onClickPrevious={onClickPrevious}
                          />
                        }
                      />
                      <Route
                        path="/saving-goals"
                        element={<SavingGoalsPageContainer />}
                      />
                      <Route
                        path="/investments"
                        element={<InvestmentsPageContainer />}
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProfilePageContainer
                            userData={sessionUser}
                            refetchUserData={refetchUserData}
                          />
                        }
                      />
                      <Route
                        path="/reports"
                        element={
                          <ReportsPageContainer userData={sessionUser} />
                        }
                      />
                      <Route
                        path="/groups"
                        element={
                          <GroupsPageContainer userData={sessionUser} />
                        }
                      />
                    </Route>
                  </>
                )}

                <Route
                  path="*"
                  element={<LoginPageContainer setToken={setToken} />}
                />
              </>
            )}
          </Routes>
        </Router>

        {/* Outside the router: every route on both the marketing site and the
            app needs to be able to answer this, not just the landing page. */}
        <CookieConsentBanner />

        <ToastContainer
          transition={Slide}
          position="bottom-center"
          autoClose={2500}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          closeButton={false}
          icon={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover={false}
          theme="colored"
        />
      </ThemeProvider>
    </div>
  );
}

export default App;
